import React, { useContext } from 'react';
import classnames from 'classnames';
import BiButton from '@wix/da-bi/pkg/Components/BiButton';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import BiSpan from '@wix/da-bi/pkg/Components/BiSpan';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import s from './Button.scss';

export interface Props {
  id?: string;
  name?: string;
  type?: 'button' | 'reset' | 'submit';
  value?: string;
  innerRef?: (node: HTMLElement | null) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  // deprecated. use className & css prop instead
  minWidth?: string;
  height?: 'small' | 'medium' | 'large';
  withPaddings?: boolean;
  className?: string;
  isMobile?: boolean;
  view?:
    | 'b0'
    | 'b1'
    | 'b1-1'
    | 'b2'
    | 'b3'
    | 'b22'
    | 'b31'
    | 'b33'
    | 'b34'
    | 'b35'
    | 'b36'
    // 'b5' use ButtonB5
    // b5-1, b5-2, b5-3 use CloseButton
    | 'b8'
    | 'b81'
    | 'b9'
    | 'b10'
    // | 'b13' use IconButton.tsx
    | 'b14'
    | 'b14-1'
    | 'b14-2'
    | 'b14-3'
    | 'b14-4'
    // | 'b16' you might wanna use AuthorWatchButton
    | 'b16-undisciplined-designers'
    | 'b18'
    | 'b19'
    // 'b19-2', 'b19-3' are HeroEditPrimaryButton and HeroEditSecondaryButton in da-user-profile
    | 'white'
    | 'white-green'
    | 'white-red'
    | 'white-black';

  href?: string;
  isFakeButton?: boolean;
  download?: boolean;
  onClick?(event): void;
  onMouseEnter?(event): void;
  onMouseLeave?(event): void;
  ['data-role']?: string;
  ['aria-label']?: string;
  ['aria-labelledby']?: string;
  ['aria-details']?: string;
  ['aria-hidden']?: boolean;
  ['aria-disabled']?: boolean;
  target?: string;
  rel?: string;
  forceHoverStateWhenDisabledBecauseReasons?: boolean;
  biData?: BiEvent;
  tabIndex?: number;
}

const Button: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  name,
  type = 'button',
  disabled,
  href,
  isFakeButton,
  onClick,
  onMouseEnter,
  onMouseLeave,
  value,
  children,
  'data-role': dataRole,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-hidden': ariaHidden,
  'aria-disabled': ariaDisabled,
  'aria-details': ariaDetails,
  biData,
  tabIndex,
  innerRef,
  target,
  rel,
  download,
  view = 'b1-1',
  withPaddings,
  height,
  className,
  forceHoverStateWhenDisabledBecauseReasons,
  fullWidth,
  minWidth,
}) => {
  const isMobile = useContext(MobileContext);

  const renderRealButton = () => (
    <BiButton
      id={id}
      name={name}
      type={type}
      disabled={disabled}
      className={getClassNames()}
      style={getInlineStyles()}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-hidden={ariaHidden}
      aria-disabled={ariaDisabled}
      aria-details={ariaDetails}
      data-role={dataRole}
      value={value}
      biData={biData}
      tabIndex={tabIndex}
      innerRef={innerRef}
    >
      {children}
    </BiButton>
  );

  const renderLinkButton = () => (
    <BiLink
      id={id}
      href={href}
      className={getClassNames()}
      style={getInlineStyles()}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-hidden={ariaHidden}
      aria-disabled={ariaDisabled}
      aria-details={ariaDetails}
      data-role={dataRole}
      target={target}
      rel={rel}
      biData={biData}
      tabIndex={tabIndex}
      download={download}
    >
      {children}
    </BiLink>
  );

  const renderFakeButton = () => (
    <BiSpan
      id={id}
      className={getClassNames()}
      style={getInlineStyles()}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-hidden={ariaHidden}
      aria-disabled={ariaDisabled}
      aria-details={ariaDetails}
      data-role={dataRole}
      biData={biData}
      role="button"
    >
      {children}
    </BiSpan>
  );

  const getClassNames = () =>
    classnames(
      s['button'],
      view && s[view],
      isFullWidth() && s['full-width'],
      disabled && s['disabled'],
      withPaddings && s['with-paddings'],
      height && s[`height-${height}`],
      forceHoverStateWhenDisabledBecauseReasons && disabled && s['force-hover-state'],
      className
    );

  const getInlineStyles = () => {
    const styles = {} as React.CSSProperties;
    if (minWidth) {
      styles.minWidth = minWidth;
    }
    return styles;
  };

  const isFullWidth = () => (fullWidth !== undefined ? fullWidth : isMobile && !minWidth);

  if (isFakeButton) {
    return renderFakeButton();
  }
  return href ? renderLinkButton() : renderRealButton();
};
Button.displayName = 'Button';

export default Button;
